import React from "react";
import PropTypes from "prop-types";
import get from "lodash/get";

import TextElement from "../TextElement";
import ImageElement from "../ImageElement";

import "../../text-story/text-story.m.css";

const ImageCard = ({ card, cardIndex, cardType, isLeadership }) => {
  const storyElements = get(card, ["story-elements"], []);
  const cardAttributes = get(card, ["metadata", "attributes"], {});
  const useBrandColour = get(cardAttributes, ["brandcolour1", 0], "") === "true";

  const groupElementsForLeftImage = (elements) =>
    elements.reduce((rows, element) => {
      const lastRow = rows[rows.length - 1];
      if (element.type === "image") {
        rows.push([element]);
      } else if (element.type === "text" && lastRow?.[0]?.type === "image") {
        lastRow.push(element);
      } else {
        rows.push([element]);
      }
      return rows;
    }, []);

  const groupElementsForRightImage = (elements) =>
    elements.reduce((rows, element) => {
      const lastRow = rows[rows.length - 1];
      if (element.type === "image") {
        rows.push([element]);
      } else if (element.type === "text" && lastRow?.[0]?.type === "image") {
        rows[rows.length - 1] = [element, ...lastRow];
      } else {
        rows.push([element]);
      }
      return rows;
    }, []);

  const groupElementsForMultiImage = (elements) => {
    const rows = [];
    let tempRow = [];

    elements.forEach((element) => {
      if (element.type === "image") {
        tempRow.push(element);

        const imageCount = tempRow.filter((el) => el.type === "image").length;
        const textCount = tempRow.filter((el) => el.type === "text").length;

        if (imageCount === 2 && textCount === 1) {
          rows.push(tempRow);
          tempRow = [];
        }
      } else if (element.type === "text") {
        const imageCount = tempRow.filter((el) => el.type === "image").length;

        if (imageCount > 0) {
          tempRow.push(element);

          const textCount = tempRow.filter((el) => el.type === "text").length;
          if (imageCount === 2 && textCount === 1) {
            rows.push(tempRow);
            tempRow = [];
          }
        } else {
          if (tempRow.length) rows.push(tempRow);
          rows.push([element]);
          tempRow = [];
        }
      } else {
        if (tempRow.length) rows.push(tempRow);
        tempRow = [];
        rows.push([element]);
      }
    });

    if (tempRow.length) rows.push(tempRow);
    return rows;
  };

  const getGroupedRows = () => {
    switch (cardType) {
      case "left-image":
        return groupElementsForLeftImage(storyElements);
      case "right-image":
        return groupElementsForRightImage(storyElements);
      case "multi-image":
        return groupElementsForMultiImage(storyElements);
      default:
        return [];
    }
  };

  const getCardStyleName = () => {
    switch (cardType) {
      case "left-image":
      case "right-image":
      case "multi-image":
        return "card image-card";
      default:
        return "card";
    }
  };

  const getRowStyleName = () => {
    switch (cardType) {
      case "left-image":
        return "left-image-row";
      case "right-image":
        return "right-image-row";
      case "multi-image":
        return "multi-image-row";
      default:
        return "";
    }
  };

  const renderElement = (element, elementIndex, row) => {
    const key = `${cardIndex}-${elementIndex}`;
    const { type } = element;

    if (type === "text") {
      return (
        <div key={key} styleName="text-element-container">
          <TextElement element={element} useBrandColour={useBrandColour} isLeadershipFocus={isLeadership} />
        </div>
      );
    }

    if (type === "image") {
      // For multi-image: first image in each row is left-image, second is right-image
      const isFirstImageInRow =
        cardType === "multi-image" &&
        row.filter((el) => el.type === "image").indexOf(element) === 0;

      return (
        <ImageElement
          key={key}
          element={element}
          hasLeftImage={cardType === "left-image" || isFirstImageInRow}
          hasRightImage={cardType === "right-image" || (cardType === "multi-image" && !isFirstImageInRow)}
          isLeadership={isLeadership}
        />
      );
    }

    return null;
  };

  const rows = getGroupedRows();

  return (
    <div styleName={getCardStyleName()}>
      {rows.map((row, rowIndex) => {
        const hasImage = row.some((el) => el.type === "image");

        if (hasImage) {
          const prevRow = rows[rowIndex - 1];
          const afterTextElement = prevRow && prevRow.every((el) => el.type === "text");

          const rowStyleName = afterTextElement
            ? `image-row ${getRowStyleName()} after-text`
            : `image-row ${getRowStyleName()}`;

          return (
            <div key={`${cardType}-${rowIndex}`} styleName={rowStyleName}>
              {row.map((element, elementIndex) =>
                renderElement(element, elementIndex, row)
              )}
            </div>
          );
        } else {
          return row.map((element, elementIndex) => {
            const key = `${cardIndex}-${elementIndex}`;
            return <TextElement key={key} element={element} useBrandColour={useBrandColour} isLeadershipFocus={isLeadership} />;
          });
        }
      })}
    </div>
  );
};

ImageCard.propTypes = {
  card: PropTypes.object.isRequired,
  cardIndex: PropTypes.number.isRequired,
  cardType: PropTypes.oneOf(["left-image", "right-image", "multi-image"]).isRequired,
  isLeadership: PropTypes.bool,
};

export default ImageCard;